import React, {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from "react"
import cx from "classnames"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { VisuallyHidden } from "../visually-hidden"
import { PageSecondaryHeaderCenterContent } from "./page-secondary-header-center-content"
import { PageSecondaryHeaderMenu } from "./page-secondary-header-menu"
import type { PageSecondaryHeaderMenuItem } from "./page-secondary-header-menu"
import type { PageSecondaryHeaderTabProps } from "./page-secondary-header-tab"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderTabsProps = {
  accessibleName: string
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderTabsStyles = [
  "-my-3",
  "hidden @min-[28rem]/center-content:flex items-center justify-center gap-6"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header tabs (panel-switching).
 *
 * Renders Base UI's `Tabs.List` (role="tablist") for switching between panels on
 * the same page. Compose with `PageSecondaryHeader.Tab` for each tab, and place
 * the corresponding `PageSecondaryHeader.TabPanel`s in the page content.
 *
 * **Requires a `PageSecondaryHeader.TabsRoot` ancestor** (Base UI's `Tabs.Root`)
 * wrapping both the page header and the panels, since they live in different
 * parts of the tree but share active-tab state via React context.
 *
 * Below `@min-[28rem]/center-content` the tabs collapse into a dropdown (`Menu`) whose
 * trigger shows the active tab. Selecting an item activates the corresponding
 * tab.
 *
 * For navigation links that *look* like tabs (cross-page navigation, not
 * panel-switching), use `PageSecondaryHeader.TabLinks` instead, which renders a
 * `<nav>` landmark with `<a>` elements and `aria-current="page"`.
 */
export function PageSecondaryHeaderTabs({
  accessibleName,
  children,
  className
}: PageSecondaryHeaderTabsProps) {
  const accessibleNameId = useId()
  const listRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const tabChildren = React.Children.toArray(children).filter(
    React.isValidElement
  ) as React.ReactElement<PageSecondaryHeaderTabProps>[]

  // The active tab value lives in Base UI's `Tabs.Root` context, which isn't
  // exposed publicly, so derive the active tab from the rendered tablist DOM
  // (`aria-selected`). Synced on mount and whenever the collapsed menu opens.
  const syncActiveTab = useCallback(() => {
    const tabs = listRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')
    if (!tabs) return
    const index = Array.from(tabs).findIndex(
      tab => tab.getAttribute("aria-selected") === "true"
    )
    if (index >= 0) setActiveIndex(index)
  }, [])

  useLayoutEffect(syncActiveTab, [syncActiveTab])

  const items: PageSecondaryHeaderMenuItem[] = tabChildren.map(
    (child, index) => ({
      key: String(child.props.value),
      label: child.props.children,
      isActive: index === activeIndex,
      // The tablist is hidden below `@min-[28rem]/center-content`, but the tab buttons stay
      // in the DOM, so activating one switches the panel via Base UI.
      onClick: () => {
        const tabs =
          listRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')
        tabs?.[index]?.click()
        setActiveIndex(index)
      }
    })
  )

  return (
    <PageSecondaryHeaderCenterContent>
      <TabsPrimitive.List
        ref={listRef}
        aria-labelledby={accessibleNameId}
        className={cx(pageSecondaryHeaderTabsStyles, className)}
        data-slot="secondary-tabs"
      >
        <VisuallyHidden id={accessibleNameId}>{accessibleName}</VisuallyHidden>
        {children}
      </TabsPrimitive.List>
      <PageSecondaryHeaderMenu
        accessibleName={accessibleName}
        items={items}
        onOpenChange={open => open && syncActiveTab()}
        dataSlot="secondary-tabs-menu"
      />
    </PageSecondaryHeaderCenterContent>
  )
}
